import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Menu, MenuItem } from "@mui/material";
import { axiosInstance } from "../../../utils/networking";
import CaseSummaryCard from "./CaseSummaryCard";
import { Chevron, RowIcon } from "./HistoryIcons";
import {
  ACCENT,
  AMBER_BG,
  AMBER_INK,
  AMBER_RULE,
  DUE_LATER,
  DUE_OVERDUE,
  DUE_SOON,
  DUE_TODAY,
  FAINT,
  INK,
  INK_2,
  MONO,
  MUTED,
  QUOTE_BG,
  RULE,
  SLATE,
} from "./styleTokens";
import {
  ALL_TYPES_LABEL,
  DEFAULT_VISIBILITY,
  DueState,
  FILTER_LABELS,
  HISTORY_FILTERS,
  HistoryFilter,
  HistoryIcon,
  HistoryRow,
  HistoryVisibility,
  INITIAL_ENTRY_LIMIT,
  TIME_WINDOWS,
  TIME_WINDOW_LABELS,
  TimeWindow,
  UPCOMING_LABEL,
  UpcomingItem,
  VISIBILITY_LABELS,
  buildBroadcastRows,
  buildRows,
  buildStats,
  buildUpcoming,
  countByCategory,
  dueState,
  filterByWindow,
  filterFeedRows,
  filterRows,
  filterUpcoming,
  groupRowsByMonth,
  mergeRows,
  rowDateLabel,
  rowDetail,
  rowIcon,
  rowTitle,
  stalenessLabel,
  statsSummary,
  upcomingDetail,
  upcomingIcon,
} from "./historyUtils";

// GET /referral/pipeline responds { referrals: [...] }; tolerate a bare array.
const unwrapPipeline = (data: any): any[] =>
  Array.isArray(data?.referrals)
    ? data.referrals
    : Array.isArray(data)
    ? data
    : [];

export interface CaseHistoryProps {
  activities: any[];
  /** Case referral list — ids or full pipeline rows; used to enrich referral rows. */
  referrals?: any[];
  caseId: string;
  caseStart?: Date | string | null;
  caseOwnerId?: string;
  initialMap?: { [userId: string]: { init: string; full: string } };
  onSelect?: (activityId: string) => void;
  onShowHistory?: () => void;
  /** Test seam — the card's "today" for grouping and relative wording. */
  now?: Date;
}

type InitialMap = { [userId: string]: { init: string; full: string } };

/** The consultant's initials from the case page's user map, "—" when unknown. */
const initialsOf = (userId: string, initialMap?: InitialMap): string => {
  const entry = userId && initialMap ? initialMap[userId] : undefined;
  return (entry && entry.init) || "—";
};

const DUE_COLORS: { [key in DueState]: string } = {
  overdue: DUE_OVERDUE,
  today: DUE_TODAY,
  soon: DUE_SOON,
  later: DUE_LATER,
};

const minutesLabel = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/* ------------------------------------------------------------------ *
 * Shared styling
 * ------------------------------------------------------------------ */

const linkSx = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontWeight: 600,
  color: ACCENT,
  "&:hover": { textDecoration: "underline" },
};

const controlSx = {
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1.45,
  minWidth: 0,
  padding: "2px 9px",
  borderRadius: "3px",
  color: MUTED,
  borderColor: RULE,
  backgroundColor: "#ffffff",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#d1d5db", backgroundColor: "#fafafa" },
};

const groupHeaderSx = {
  fontFamily: MONO,
  fontSize: "11px",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: MUTED,
  padding: "12px 0 4px",
  borderBottom: `1px solid ${RULE}`,
};

const ellipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const checkboxLabelSx = {
  margin: 0,
  marginLeft: "2px",
  "& .MuiFormControlLabel-label": {
    fontSize: "12px",
    fontWeight: 500,
    color: MUTED,
    lineHeight: 1.45,
  },
};

const checkboxSx = {
  padding: "2px 4px 2px 2px",
  color: SLATE,
  "&.Mui-checked": { color: ACCENT },
  "& .MuiSvgIcon-root": { fontSize: "16px" },
};

/* ------------------------------------------------------------------ *
 * One single-line row: type · initials · description …… date
 * ------------------------------------------------------------------ */

interface FeedRowProps {
  icon: HistoryIcon;
  /** Activity type, bold. */
  title: string;
  /** Consultant initials, monospace. */
  who?: string;
  /** Short description; takes the remaining width and truncates. */
  detail?: string;
  date: string;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** Lifecycle rows read back a shade. */
  muted?: boolean;
  /** Colours the glyph and the detail — due-date closeness on upcoming rows. */
  tint?: string;
  /** The very last row of the card drops its hairline. */
  hideRule?: boolean;
  children?: React.ReactNode;
}

const Separator = () => (
  <Box component="span" aria-hidden="true" sx={{ color: FAINT, flexShrink: 0 }}>
    ·
  </Box>
);

const FeedRow = ({
  icon,
  title,
  who,
  detail,
  date,
  expandable = false,
  expanded = false,
  onToggle,
  muted = false,
  tint,
  hideRule = false,
  children,
}: FeedRowProps) => (
  <Box>
    <Box
      component={expandable ? "button" : "div"}
      type={expandable ? "button" : undefined}
      aria-expanded={expandable ? expanded : undefined}
      onClick={expandable ? onToggle : undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: "18px minmax(0, 1fr) auto 14px",
        gap: "10px",
        alignItems: "center",
        width: "100%",
        margin: 0,
        padding: "7px 2px",
        textAlign: "left",
        background: "none",
        border: 0,
        borderBottom: hideRule ? 0 : `1px solid ${RULE}`,
        borderRadius: 0,
        font: "inherit",
        cursor: expandable ? "pointer" : "default",
        "&:hover": expandable ? { backgroundColor: "#fafafa" } : {},
      }}
    >
      <Box
        component="span"
        sx={{ display: "inline-flex", color: tint || (muted ? FAINT : SLATE) }}
      >
        <RowIcon name={icon} />
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          alignItems: "baseline",
          gap: "6px",
          fontSize: "13px",
          lineHeight: 1.4,
        }}
      >
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            color: muted ? MUTED : INK,
            flexShrink: 0,
            maxWidth: "60%",
            ...ellipsis,
          }}
        >
          {title}
        </Box>
        {who ? (
          <>
            <Separator />
            <Box
              component="span"
              sx={{
                fontFamily: MONO,
                fontSize: "11.5px",
                color: MUTED,
                flexShrink: 0,
              }}
            >
              {who}
            </Box>
          </>
        ) : null}
        {detail ? (
          <>
            <Separator />
            <Box
              component="span"
              sx={{
                color: tint || MUTED,
                fontWeight: tint ? 600 : 400,
                minWidth: 0,
                ...ellipsis,
              }}
            >
              {detail}
            </Box>
          </>
        ) : null}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: "11.5px",
          color: MUTED,
          whiteSpace: "nowrap",
        }}
      >
        {date}
      </Box>
      <Box component="span" sx={{ display: "inline-flex", color: FAINT }}>
        {expandable ? <Chevron open={expanded} /> : null}
      </Box>
    </Box>
    {expandable && expanded ? (
      <Box
        sx={{
          margin: "0 0 8px 28px",
          padding: "8px 10px",
          borderLeft: `3px solid ${SLATE}`,
          borderRadius: "0 3px 3px 0",
          backgroundColor: QUOTE_BG,
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        {children}
      </Box>
    ) : null}
  </Box>
);

/* ------------------------------------------------------------------ *
 * An activity row
 * ------------------------------------------------------------------ */

interface ActivityRowProps {
  row: HistoryRow;
  expanded: boolean;
  onToggle: () => void;
  onSelect?: (activityId: string) => void;
  initialMap?: InitialMap;
  now: Date;
  hideRule?: boolean;
}

const ActivityRow = ({
  row,
  expanded,
  onToggle,
  onSelect,
  initialMap,
  now,
  hideRule,
}: ActivityRowProps) => {
  if (row.minor) {
    return (
      <FeedRow
        icon="dot"
        title={rowTitle(row)}
        who={initialsOf(row.userId, initialMap)}
        date={rowDateLabel(row.date, now)}
        muted
        hideRule={hideRule}
      />
    );
  }

  const times: string[] = [];
  if (row.contactTime) times.push(`Contact ${minutesLabel(row.contactTime)}`);
  if (row.prepTime) times.push(`Prep ${minutesLabel(row.prepTime)}`);
  if (row.travelTime) times.push(`Travel ${minutesLabel(row.travelTime)}`);
  const meta = [row.method, ...times].filter(Boolean).join(" · ");
  // A row with no note, no method/time and no Edit link has nothing to open.
  const hasBody = !!row.note || !!meta || !!(row.editable && onSelect);

  return (
    <FeedRow
      icon={rowIcon(row)}
      title={rowTitle(row)}
      who={initialsOf(row.userId, initialMap)}
      detail={rowDetail(row)}
      date={rowDateLabel(row.date, now)}
      expandable={hasBody}
      expanded={expanded}
      onToggle={onToggle}
      muted={row.category === "bulk"}
      hideRule={hideRule}
    >
      {row.note ? (
        <Box sx={{ fontSize: "13px", lineHeight: 1.55, color: INK_2, whiteSpace: "pre-wrap" }}>
          {row.note}
        </Box>
      ) : null}
      {meta ? <Box sx={{ fontSize: "11.5px", color: FAINT }}>{meta}</Box> : null}
      {row.editable && onSelect ? (
        <Box sx={{ fontSize: "12px" }}>
          <Box component="button" type="button" onClick={() => onSelect(row.id)} sx={linkSx}>
            Edit
          </Box>
        </Box>
      ) : null}
    </FeedRow>
  );
};

/* ------------------------------------------------------------------ *
 * The card
 * ------------------------------------------------------------------ */

const CaseHistory = ({
  activities,
  referrals,
  caseId,
  caseOwnerId,
  initialMap,
  onSelect,
  onShowHistory,
  now,
}: CaseHistoryProps) => {
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("all");
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<{ [id: string]: boolean }>({});
  const [showAll, setShowAll] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<HistoryVisibility>(DEFAULT_VISIBILITY);

  const toggleVisibility = (key: keyof HistoryVisibility) => {
    // Unticking bulk mail while its menu entry is selected would strand the
    // feed on an empty category.
    if (key === "bulkEmails" && visibility.bulkEmails && filter === "bulk") {
      setFilter("all");
    }
    setVisibility((current) => ({ ...current, [key]: !current[key] }));
  };

  const today = useMemo(() => now || new Date(), [now]);

  // The case page keeps its referral list as bare ids, which carry no partner
  // name or pipeline status. When that is all we are handed, pull the same
  // per-case pipeline rows ClientWorkflow already uses so referral rows can
  // show "Referral · Partner". Failure is silent — rows just lose the hint.
  const [pipeline, setPipeline] = useState<any[]>([]);
  const referralObjects = useMemo(
    () =>
      (referrals || []).filter(
        (referral: any) => referral && typeof referral === "object" && referral._id,
      ),
    [referrals],
  );

  useEffect(() => {
    if (!caseId || referralObjects.length > 0) return undefined;
    let alive = true;
    axiosInstance
      .get(`/referral/pipeline?case_id=${caseId}`)
      .then((res: any) => {
        if (alive) setPipeline(unwrapPipeline(res?.data));
      })
      .catch(() => {
        if (alive) setPipeline([]);
      });
    return () => {
      alive = false;
    };
  }, [caseId, referralObjects.length]);

  // What is due on this case: pending reminders + incomplete dated Action Item
  // tasks. Both failures are silent — the group just disappears.
  const [reminders, setReminders] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);

  useEffect(() => {
    if (!caseId) return undefined;
    let alive = true;
    axiosInstance
      .get(`/reminder?status=all&case_id=${caseId}`)
      .then((res: any) => {
        const list = res?.data?.reminders;
        if (alive) setReminders(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setReminders([]);
      });
    axiosInstance
      .get(`/actionPlan?case_id=${caseId}`)
      .then((res: any) => {
        const list = res?.data?.action_plans;
        if (alive) setPlans(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setPlans([]);
      });
    return () => {
      alive = false;
    };
  }, [caseId]);

  // Bulk mail (the "Event/broadcast emails" checkbox): broadcasts write no
  // activity, so their paper-trail rows — plus the subjects of the legacy
  // "Event Invitation" activities — come from GET /broadcast/case-history,
  // fetched only once the box is first ticked. Failure is silent.
  const [bulkMail, setBulkMail] = useState<{
    caseId: string;
    broadcasts: any[];
    invitations: { [emailId: string]: string };
  } | null>(null);

  useEffect(() => {
    if (!caseId || !visibility.bulkEmails || bulkMail?.caseId === caseId) {
      return undefined;
    }
    let alive = true;
    axiosInstance
      .get(`/broadcast/case-history?case_id=${caseId}`)
      .then((res: any) => {
        if (!alive) return;
        const data = res?.data || {};
        setBulkMail({
          caseId,
          broadcasts: Array.isArray(data.broadcasts) ? data.broadcasts : [],
          invitations:
            data.invitations && typeof data.invitations === "object"
              ? data.invitations
              : {},
        });
      })
      .catch(() => {
        if (alive) setBulkMail({ caseId, broadcasts: [], invitations: {} });
      });
    return () => {
      alive = false;
    };
  }, [caseId, visibility.bulkEmails, bulkMail]);

  const referralRows = referralObjects.length > 0 ? referralObjects : pipeline;

  const bulkForCase = bulkMail && bulkMail.caseId === caseId ? bulkMail : null;
  const rows = useMemo(() => {
    const activityRows = buildRows(activities || [], referralRows, {
      bulkEmails: visibility.bulkEmails,
      invitationSubjects: bulkForCase?.invitations,
    });
    return visibility.bulkEmails && bulkForCase
      ? mergeRows(activityRows, buildBroadcastRows(bulkForCase.broadcasts))
      : activityRows;
  }, [activities, referralRows, visibility.bulkEmails, bulkForCase]);
  // What the Activities / Event-broadcast checkboxes let into the feed.
  const feedRows = useMemo(() => filterFeedRows(rows, visibility), [rows, visibility]);
  const showFeed = visibility.activities || visibility.bulkEmails;
  const filterOptions = visibility.bulkEmails
    ? [...HISTORY_FILTERS, "bulk" as HistoryFilter]
    : HISTORY_FILTERS;
  // The AI summary is cached server-side on the live activity count, so the
  // refetch trigger must count every non-deleted activity, not just the
  // subset the feed chooses to show.
  const liveActivityCount = useMemo(
    () =>
      (Array.isArray(activities) ? activities : []).filter(
        (a: any) => a && a.status !== "deleted",
      ).length,
    [activities],
  );
  const counts = useMemo(() => countByCategory(feedRows), [feedRows]);
  const stats = useMemo(() => buildStats(rows), [rows]);
  const stale = stalenessLabel(stats, today);

  const upcoming = useMemo(
    () => filterUpcoming(buildUpcoming(reminders, plans, caseOwnerId || ""), visibility),
    [reminders, plans, caseOwnerId, visibility],
  );

  const entries = useMemo(
    () => filterByWindow(filterRows(feedRows, filter), timeWindow, today),
    [feedRows, filter, timeWindow, today],
  );
  const visible = showAll ? entries : entries.slice(0, INITIAL_ENTRY_LIMIT);
  const hiddenCount = entries.length - visible.length;
  const groups = useMemo(() => groupRowsByMonth(visible), [visible]);

  const isOpen = (id: string): boolean => Boolean(expanded[id]);

  const toggle = (id: string) =>
    setExpanded((current) => ({ ...current, [id]: !isOpen(id) }));

  const renderRow = (row: HistoryRow, hideRule: boolean) => (
    <ActivityRow
      key={row.id}
      row={row}
      expanded={isOpen(row.id)}
      onToggle={() => toggle(row.id)}
      onSelect={onSelect}
      initialMap={initialMap}
      now={today}
      hideRule={hideRule}
    />
  );

  const renderUpcoming = (item: UpcomingItem) => {
    const expandableItem = !!item.note;
    const state = dueState(item.due, today);
    return (
      <FeedRow
        key={item.id}
        icon={upcomingIcon(item)}
        title={item.title}
        who={initialsOf(item.ownerId, initialMap)}
        detail={upcomingDetail(item, today)}
        tint={DUE_COLORS[state]}
        date={rowDateLabel(item.due, today)}
        expandable={expandableItem}
        expanded={expandableItem && isOpen(item.id)}
        onToggle={() => toggle(item.id)}
      >
        <Box sx={{ fontSize: "13px", lineHeight: 1.55, color: INK_2, whiteSpace: "pre-wrap" }}>
          {item.note}
        </Box>
      </FeedRow>
    );
  };

  const typeLabel = filter === "all" ? ALL_TYPES_LABEL : FILTER_LABELS[filter];

  const summaryFooter = (
    <>
      <Box component="span">{statsSummary(stats, today)}</Box>
      {stale ? (
        <Box
          component="span"
          sx={{
            backgroundColor: AMBER_BG,
            border: `1px solid ${AMBER_RULE}`,
            color: AMBER_INK,
            borderRadius: "3px",
            padding: "1px 6px",
            fontSize: "11px",
            fontWeight: 600,
          }}
        >
          {stale}
        </Box>
      ) : null}
      {onShowHistory ? (
        <Box component="button" type="button" onClick={onShowHistory} sx={linkSx}>
          Touch history
        </Box>
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        border: `1px solid ${RULE}`,
        borderRadius: "5px",
        backgroundColor: "#ffffff",
        padding: "11px 13px 4px",
        display: "flex",
        flexDirection: "column",
        gap: "9px",
      }}
    >
      <CaseSummaryCard caseId={caseId} activityCount={liveActivityCount} footer={summaryFooter} />

      {/* controls: centred, so the 40 px touch checkboxes don't stretch the
          buttons beside them */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" }}>
        <Button
          variant="outlined"
          size="small"
          sx={controlSx}
          onClick={(event) => setTypeAnchor(event.currentTarget)}
        >
          {typeLabel} ▾
        </Button>
        <Menu
          anchorEl={typeAnchor}
          open={!!typeAnchor}
          onClose={() => setTypeAnchor(null)}
        >
          {filterOptions.map((key) => (
            <MenuItem
              key={key}
              selected={filter === key}
              onClick={() => {
                setFilter(key);
                setShowAll(false);
                setTypeAnchor(null);
              }}
              sx={{
                fontSize: "13px",
                gap: "18px",
                justifyContent: "space-between",
                color: filter === key ? ACCENT : INK_2,
                fontWeight: filter === key ? 600 : 400,
              }}
            >
              <span>{FILTER_LABELS[key]}</span>
              <Box component="span" sx={{ fontFamily: MONO, fontSize: "11.5px", color: MUTED }}>
                {counts[key]}
              </Box>
            </MenuItem>
          ))}
        </Menu>

        <Button
          variant="outlined"
          size="small"
          sx={controlSx}
          onClick={(event) => setTimeAnchor(event.currentTarget)}
        >
          {TIME_WINDOW_LABELS[timeWindow]} ▾
        </Button>
        <Menu
          anchorEl={timeAnchor}
          open={!!timeAnchor}
          onClose={() => setTimeAnchor(null)}
        >
          {TIME_WINDOWS.map((key) => (
            <MenuItem
              key={key}
              selected={timeWindow === key}
              onClick={() => {
                setTimeWindow(key);
                setShowAll(false);
                setTimeAnchor(null);
              }}
              sx={{
                fontSize: "13px",
                color: timeWindow === key ? ACCENT : INK_2,
                fontWeight: timeWindow === key ? 600 : 400,
              }}
            >
              {TIME_WINDOW_LABELS[key]}
            </MenuItem>
          ))}
        </Menu>

        {(Object.keys(VISIBILITY_LABELS) as (keyof HistoryVisibility)[]).map((key) => (
          <FormControlLabel
            key={key}
            sx={checkboxLabelSx}
            label={VISIBILITY_LABELS[key]}
            control={
              <Checkbox
                size="small"
                sx={checkboxSx}
                checked={visibility[key]}
                onChange={() => toggleVisibility(key)}
              />
            }
          />
        ))}
      </Box>

      {/* upcoming & overdue */}
      {upcoming.length > 0 ? (
        <Box>
          <Box sx={groupHeaderSx}>{UPCOMING_LABEL}</Box>
          {upcoming.map(renderUpcoming)}
        </Box>
      ) : null}

      {/* the feed */}
      {!showFeed ? null : groups.length === 0 ? (
        <Box sx={{ fontSize: "13px", color: FAINT, padding: "10px 2px" }}>
          {feedRows.length > 0
            ? "Nothing in this view."
            : !visibility.activities
            ? "No event or broadcast emails for this business."
            : "No activity recorded yet."}
        </Box>
      ) : (
        groups.map((group, index) => (
          <Box key={`${group.key}-${index}`}>
            <Box sx={groupHeaderSx}>{group.label}</Box>
            {group.rows.map((row, position) =>
              renderRow(
                row,
                hiddenCount === 0 &&
                  index === groups.length - 1 &&
                  position === group.rows.length - 1,
              ),
            )}
          </Box>
        ))
      )}

      {showFeed && hiddenCount > 0 ? (
        <Box
          component="button"
          type="button"
          onClick={() => setShowAll(true)}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            padding: "9px 2px 9px 28px",
            background: "none",
            border: 0,
            cursor: "pointer",
            font: "inherit",
            fontSize: "12px",
            fontWeight: 600,
            color: ACCENT,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Show {hiddenCount} older
        </Box>
      ) : null}
    </Box>
  );
};

export default CaseHistory;
